import { Slider } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { formatThreshold } from '@/shared/lib/utils';

type Props = {
  /** Подпись слайдера (что за порог) */
  label: string;
  /** Доступные пороги близости — шаги ползунка (индексы массива) */
  thresholds: number[];
  /** Текущий порог близости; `undefined` — ещё не инициализирован */
  value: number | undefined;
  /** Смена порога — прокидывает конкретное значение из `thresholds` */
  onChange: (value: number) => void;
  /** Подпись левого края шкалы (крупнее понятия) */
  coarserLabel: string;
  /** Подпись правого края шкалы (точнее) */
  finerLabel: string;
  /** Класс внешнего контейнера */
  className?: string;
};

/**
 * Дискретный ползунок «порога близости»: шаги равны индексам массива `thresholds`,
 * текущее значение показано крупно, подпись поясняет направление «крупнее ← → точнее».
 * Пока пороги не пришли (`thresholds` пуст) — ничего не рендерит; при единственном
 * значении ползунок задизейблен.
 */
export const ThresholdSlider: FC<Props> = ({
  label,
  thresholds,
  value,
  onChange,
  coarserLabel,
  finerLabel,
  className,
}) => {
  // Индекс текущего порога в массиве шагов; при отсутствии — середина набора.
  const currentIndex = useMemo(() => {
    const found = value === undefined ? -1 : thresholds.indexOf(value);
    return found >= 0 ? found : Math.floor(thresholds.length / 2);
  }, [thresholds, value]);

  // Пока пороги не пришли — слайдер не показываем.
  if (thresholds.length === 0) return null;

  const maxIndex = Math.max(thresholds.length - 1, 0);

  return (
    <div className={className ?? 'flex flex-col gap-2 lg:w-72'}>
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className="text-2xl font-semibold text-foreground tabular-nums">{formatThreshold(value)}</span>
      </div>
      <Slider
        value={[currentIndex]}
        onValueChange={(v: number[]) => {
          const next = thresholds[v[0] ?? 0];
          if (next !== undefined) onChange(next);
        }}
        min={0}
        max={maxIndex}
        step={1}
        disabled={thresholds.length <= 1}
      />
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{coarserLabel}</span>
        <span>{finerLabel}</span>
      </div>
    </div>
  );
};
